// ============================================================================
// RotatingQuotes — the fading testimonial carousel on the marketing home.
// ----------------------------------------------------------------------------
// Fetches admin-managed quotes from /api/quotes and rotates through them with
// a soft cross-fade (400ms opacity transition). Auto-advances every 4s.
//
// If the fetch fails or returns zero quotes, we render nothing — the parent
// section keeps its own layout intact.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import logger from "../lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROTATE_MS = 4000;      // 4-second rotation cadence
const FADE_MS = 450;         // dissolve duration; must match CSS transition

// ─── Client-side cache ─────────────────────────────────────────────────────
// Stale-while-revalidate: read cached quotes from localStorage synchronously
// so the first paint has content, then refresh in the background. Cache
// expires after CACHE_TTL_MS — but even fresh, we still revalidate on mount
// so admin edits are picked up within one page load.
const CACHE_KEY = "bc_quotes_cache_v1";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const readCache = () => {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.quotes || !Array.isArray(parsed.quotes)) return null;
    // Expired entries are still returned for the initial paint (better than
    // rendering nothing) — the background revalidate replaces them shortly.
    return parsed;
  } catch {
    return null;
  }
};

const writeCache = (quotes) => {
  try {
    window.localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ quotes, ts: Date.now() }),
    );
  } catch {
    /* quota / private mode — ignore */
  }
};

const isFresh = (entry) => entry && Date.now() - (entry.ts || 0) < CACHE_TTL_MS;

export const RotatingQuotes = ({ className = "", testId = "rotating-quotes", bypassCache = false }) => {
  // Seed state synchronously from cache so the first paint has content.
  // Admin preview passes bypassCache to always show the freshest data.
  const [quotes, setQuotes] = useState(() => (bypassCache ? [] : readCache()?.quotes || []));
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const timerRef = useRef(null);

  // Fetch on mount. Skip network entirely if the cache is still fresh —
  // saves anonymous marketing visitors an unnecessary API call.
  useEffect(() => {
    const cached = readCache();
    if (!bypassCache && isFresh(cached)) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/quotes`);
        if (cancelled) return;
        const fresh = res.data?.quotes || [];
        setQuotes(fresh);
        if (!bypassCache) writeCache(fresh);
      } catch (err) {
        // Ignore navigation-cancellation: axios throws when a component
        // unmounts mid-flight (user clicks away from the homepage before
        // the quotes endpoint responds). Not a real error — surfacing it
        // just spams the admin alert inbox.
        const isAbort =
          cancelled ||
          err?.code === "ERR_CANCELED" ||
          err?.name === "CanceledError" ||
          axios.isCancel?.(err) ||
          /aborted|canceled|cancelled/i.test(err?.message || "");
        if (isAbort) return;
        logger.error("Failed to load rotating quotes", err);
      }
    })();
    return () => { cancelled = true; };
  }, [bypassCache]);

  // Advance one step. Wrapped in useCallback so we can call it manually
  // (future: prev/next arrows) without recreating on every render.
  const advance = useCallback(() => {
    setVisible(false);
    // Wait for fade-out to finish before swapping the text — otherwise
    // users see the letters change mid-dissolve which looks janky.
    setTimeout(() => {
      setIndex((i) => (quotes.length ? (i + 1) % quotes.length : 0));
      setVisible(true);
    }, FADE_MS);
  }, [quotes.length]);

  // Auto-rotate. Skip when < 2 quotes (nothing to rotate to).
  useEffect(() => {
    if (quotes.length < 2) return undefined;
    timerRef.current = setInterval(advance, ROTATE_MS);
    return () => clearInterval(timerRef.current);
  }, [quotes.length, advance]);

  if (!quotes.length) return null;

  const current = quotes[index] || quotes[0];

  return (
    <div
      className={className}
      data-testid={testId}
      aria-live="polite"
      // Reserve vertical space so the surrounding page doesn't jump when a
      // shorter quote rotates in after a longer one. min-h scales with the
      // font size hierarchy used below.
      style={{ minHeight: "10em" }}
    >
      <div
        className="transition-opacity ease-in-out"
        style={{
          transitionDuration: `${FADE_MS}ms`,
          opacity: visible ? 1 : 0,
        }}
      >
        <blockquote
          className="font-outfit text-2xl sm:text-3xl lg:text-4xl text-[#1B2349] leading-snug tracking-tight"
          data-testid={`${testId}-text`}
        >
          &ldquo;{current.text}&rdquo;
        </blockquote>
        {current.attribution ? (
          <p
            className="mt-7 text-[#718096]"
            data-testid={`${testId}-attribution`}
          >
            {current.attribution}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default RotatingQuotes;
